import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Coins, Globe, Crown, MapPin, ChevronDown } from 'lucide-react';
import { getCreditPackages, purchaseCredits } from '../services/creditApiClient';
import { CreditPackage } from '../types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import SubscriptionBadge from './SubscriptionBadge';
import { getSubscription, isSubscriptionActive } from '../services/subscriptionApiClient';
import { useGeoLocation } from '../hooks/useGeoLocation';

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Internal component to handle lock state
const SubscriptionLockOverlay = ({ onUnlock }: { onUnlock: () => void }) => {
  const [isLocked, setIsLocked] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { subscription } = await getSubscription();
        const active = subscription && isSubscriptionActive(subscription.status);
        setIsLocked(!active);
      } catch (e) {
        console.error("Failed to check subscription status", e);
        setIsLocked(false);
      } finally {
        setChecking(false);
      }
    };
    checkStatus();
  }, []);

  if (checking) return null;
  if (!isLocked) return null;

  return (
    <div className="absolute inset-0 z-20 backdrop-blur-md bg-black/60 flex flex-col items-center justify-center rounded-xl border border-white/5">
      <div className="p-4 bg-[#18181b] rounded-full mb-4 border border-white/10 shadow-xl">
        <Crown className="w-8 h-8 text-amber-500" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">Subscription Required</h3>
      <p className="text-zinc-400 text-center max-w-sm mb-6 px-4">
        Exclusive access: You must be subscribed to the Starter Plan to purchase additional credit packs.
      </p>
      <button
        onClick={onUnlock}
        className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg shadow-lg hover:shadow-amber-500/20 transition-all active:scale-95"
      >
        Subscribe Now
      </button>
    </div>
  );
};

const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({ isOpen, onClose }) => {
  const { refreshCredits } = useAuth();
  const { isAlgeria, loading: geoLoading } = useGeoLocation();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  // Default to USD for everyone, DZD only shown as secondary for Algerians
  const [selectedCurrency, setSelectedCurrency] = useState<'DZD' | 'USD'>('USD');
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'credits' | 'subscription'>('credits');
  const [showLocalPayment, setShowLocalPayment] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPackages();
      // Reset to USD when modal opens
      setSelectedCurrency('USD');
      setShowLocalPayment(false);
    }
  }, [isOpen]);

  const loadPackages = async () => {
    try {
      const data = await getCreditPackages();
      setPackages(data);
    } catch (error) {
      console.error('Failed to load packages:', error);
      toast.error('Failed to load credit packages');
    }
  };

  const filteredPackages = packages.filter(pkg => pkg.currency === selectedCurrency);

  const handleSelectPackage = async (pkg: CreditPackage) => {
    setSelectedPackage(pkg);
    setLoading(true);

    try {
      const result = await purchaseCredits(pkg.id);

      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        toast.error('Failed to create payment session');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Purchase failed:', error);
      toast.error(error.message || 'Failed to initiate checkout');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPackage(null);
    setShowLocalPayment(false);
    onClose();
  };

  const handleSwitchToLocal = () => {
    setSelectedCurrency('DZD');
    setShowLocalPayment(true);
  };

  const handleSwitchToInternational = () => {
    setSelectedCurrency('USD');
    setShowLocalPayment(false);
  };

  const CheckIcon = () => (
    <svg viewBox="0 0 12 10" className="w-2.5 h-2.5 fill-current">
      <path d="M1 5l3.5 3.5L11 1"></path>
    </svg>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#050505] border border-white/10 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl modal-scrollbar"
          >
            {/* Enhanced Header with subtle gradient animation */}
            <div className="sticky top-0 bg-gradient-to-b from-[#050505] via-[#050505] to-[#050505]/95 backdrop-blur border-b border-white/10 p-6 flex flex-col gap-4 z-10">
              {/* Subtle animated glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[2px] bg-gradient-to-r from-transparent via-teal-500/50 to-transparent opacity-60" />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-teal-500/20 to-emerald-500/10 rounded-xl border border-teal-500/20">
                    <Coins className="w-6 h-6 text-teal-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Get Credits</h2>
                    <p className="text-sm text-zinc-400">Subscribe monthly or buy credit packs</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2.5 hover:bg-white/10 rounded-xl transition-colors group"
                >
                  <X className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" />
                </button>
              </div>

              {/* Tab Switcher */}
              <div className="flex bg-[#18181b] p-1 rounded-xl border border-white/5 self-start">
                <button
                  onClick={() => setActiveTab('subscription')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'subscription'
                    ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 shadow-sm ring-1 ring-amber-500/30'
                    : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                >
                  <Crown className="w-4 h-4" />
                  Subscribe & Save
                </button>
                <button
                  onClick={() => setActiveTab('credits')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'credits'
                    ? 'bg-[#27272a] text-white shadow-sm ring-1 ring-white/10'
                    : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                >
                  <Coins className="w-4 h-4" />
                  Buy Credits
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Subscription Tab */}
              {activeTab === 'subscription' && (
                <div className="space-y-6">
                  <SubscriptionBadge variant="full" showManageButton={true} />

                  <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-4">
                    <h3 className="text-sm font-medium text-zinc-400 mb-3">Why Subscribe?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-zinc-300">20 welcome credits on signup</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-zinc-300">15 bonus credits every month</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-zinc-300">Cancel anytime, keep your credits</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-center text-sm text-zinc-500">
                    Need more credits? Switch to the "Buy Credits" tab for one-time purchases.
                  </p>
                </div>
              )}

              {/* Credits Tab */}
              {activeTab === 'credits' && (
                <div className="relative">
                  {/* Lock Screen for Non-Subscribers */}
                  <SubscriptionLockOverlay
                    onUnlock={() => setActiveTab('subscription')}
                  />

                  {/* Show back button if viewing local payment */}
                  {showLocalPayment && (
                    <div className="flex justify-center mb-6">
                      <button
                        onClick={handleSwitchToInternational}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
                      >
                        <Globe className="w-4 h-4" />
                        ← Back to International (USD)
                      </button>
                    </div>
                  )}

                  {/* Current payment type indicator */}
                  {!showLocalPayment && (
                    <div className="flex items-center justify-center gap-2 mb-6">
                      <Globe className="w-4 h-4 text-teal-400" />
                      <span className="text-sm text-zinc-300">International Payments (USD)</span>
                    </div>
                  )}

                  {showLocalPayment && (
                    <div className="flex items-center justify-center gap-2 mb-6">
                      <span className="text-xl">🇩🇿</span>
                      <span className="text-sm text-zinc-300">Local Algerian Payment (DZD)</span>
                    </div>
                  )}

                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-zinc-400">Redirecting to secure payment...</p>
                    </div>
                  ) : (
                    <>
                      {/* Package Selection */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {filteredPackages.map((pkg) => {
                          const isPopular = pkg.popular;

                          return (
                            <motion.div
                              key={pkg.id}
                              whileTap={{ scale: 0.98 }}
                              whileHover={{ y: -4 }}
                              onClick={() => handleSelectPackage(pkg)}
                              className={`
                                bg-[#101012] rounded-2xl p-8 flex flex-col relative transition-all duration-300 overflow-hidden cursor-pointer
                                ${isPopular
                                  ? 'border border-[#384245] hover:border-[#4a5558]'
                                  : 'border border-[#27272a] hover:border-[#3f3f46]'
                                }
                              `}
                            >
                              {/* Top light reflection for popular */}
                              {isPopular && (
                                <>
                                  <div className="absolute top-0 left-0 right-0 h-[1px] opacity-70" style={{
                                    background: 'linear-gradient(90deg, transparent, #2dd4bf, transparent)'
                                  }}></div>

                                  {/* Top Spotlight Glow */}
                                  <div className="absolute -top-[50px] left-1/2 transform -translate-x-1/2 w-[200px] h-[100px] pointer-events-none" style={{
                                    background: 'radial-gradient(circle, rgba(45,212,191,0.15) 0%, transparent 70%)',
                                    filter: 'blur(20px)'
                                  }}></div>
                                </>
                              )}

                              <div className="flex items-center justify-between mb-6 relative z-10">
                                <span className="text-xl font-medium text-white">
                                  {pkg.credits === 15 ? 'Starter Artisan' : pkg.credits === 35 ? 'Professional Clinic' : 'Prestige Enterprise'}
                                </span>
                                {isPopular && (
                                  <span className="bg-[rgba(255,255,255,0.1)] text-[#e5e7eb] text-xs px-3 py-1.5 rounded-full font-medium border border-[rgba(255,255,255,0.1)]">
                                    Most Popular
                                  </span>
                                )}
                              </div>

                              <div className="mb-4 relative z-10">
                                <div className="flex items-baseline gap-2">
                                  {pkg.currency === 'DZD' ? (
                                    <span className="text-5xl font-semibold text-white tracking-tight">
                                      {pkg.price.toLocaleString()} <span className="text-xl">DZD</span>
                                    </span>
                                  ) : (
                                    <span className="text-5xl font-semibold text-white tracking-tight">
                                      <span className="text-3xl align-top">$</span>{pkg.price}
                                    </span>
                                  )}
                                  <span className="text-lg text-[#9CA3AF]">/ {pkg.credits} credits</span>
                                </div>
                              </div>

                              <p className="text-[#9CA3AF] leading-relaxed mb-8 min-h-[50px] relative z-10">
                                {pkg.credits <= 15
                                  ? 'Trial / Occasional Case'
                                  : pkg.credits <= 35
                                    ? 'Active Practice (3-5 cases/week)'
                                    : 'High-Volume Dental Group'}
                              </p>

                              <button className={`
                                w-full py-4 rounded-lg text-sm font-semibold transition-all duration-200 relative z-10
                                ${isPopular
                                  ? 'text-white hover:opacity-95'
                                  : 'bg-[rgba(255,255,255,0.03)] border border-[#27272a] text-white font-medium hover:bg-[rgba(255,255,255,0.08)] hover:border-[#52525b]'
                                }
                              `}
                                style={isPopular ? {
                                  background: 'linear-gradient(180deg, #2dd4bf 0%, #0d9488 100%)',
                                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 20px rgba(45,212,191,0.25)'
                                } : {}}
                              >
                                Buy {pkg.credits} Credits
                              </button>

                              <div className="flex items-center my-8 relative z-10">
                                <div className="flex-1 h-[1px] bg-[#27272a]"></div>
                                <span className="px-4 text-xs tracking-widest text-[#52525b] font-semibold uppercase">Credit Costs</span>
                                <div className="flex-1 h-[1px] bg-[#27272a]"></div>
                              </div>

                              <p className="text-sm text-[#71717a] mb-5 relative z-10">Use credits for all generations:</p>

                              <ul className="space-y-4 relative z-10">
                                {[
                                  { name: 'Smile Generation', cost: '1 Credit', free: false },
                                  { name: 'Video Generation', cost: '5 Credits', free: false },
                                  { name: 'Showcase Portrait', cost: '1 Credit', free: false },
                                  { name: 'Prestige PDF', cost: '0.5 Credit', free: false },
                                  { name: 'Normal PDF', cost: 'Free', free: true }
                                ].map((item, i) => (
                                  <li key={i} className="flex items-center justify-between text-sm text-[#d4d4d8]">
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isPopular
                                          ? ''
                                          : 'bg-[#27272a] border border-[#3f3f46]'
                                          }`}
                                        style={isPopular ? {
                                          backgroundColor: 'rgba(45,212,191,0.15)',
                                          borderColor: 'rgba(45,212,191,0.3)',
                                          borderWidth: '1px',
                                          color: '#2dd4bf'
                                        } : {}}
                                      >
                                        <span className={isPopular ? 'text-[#2dd4bf]' : 'text-[#71717a]'}>
                                          <CheckIcon />
                                        </span>
                                      </div>
                                      {item.name}
                                    </div>
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${item.free
                                      ? 'text-[#2dd4bf] bg-[rgba(45,212,191,0.1)] border-[rgba(45,212,191,0.2)]'
                                      : 'text-white bg-[#27272a] border-[#3f3f46]'
                                      }`}>
                                      {item.cost}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* Algerian Local Payment Option - Only visible for Algerian users */}
                      {isAlgeria && !geoLoading && !showLocalPayment && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="mt-8 pt-6 border-t border-white/5"
                        >
                          <div className="flex flex-col items-center">
                            <button
                              onClick={handleSwitchToLocal}
                              className="group flex items-center gap-3 px-5 py-3 rounded-xl bg-gradient-to-r from-white/[0.03] to-white/[0.01] border border-white/10 hover:border-white/20 transition-all duration-300"
                            >
                              <span className="text-xl">🇩🇿</span>
                              <div className="flex flex-col items-start">
                                <span className="text-sm font-medium text-zinc-300 group-hover:text-white transition-colors">
                                  In Algeria? Pay locally in DZD
                                </span>
                                <span className="text-xs text-zinc-500">
                                  Use Chargily for local payment options
                                </span>
                              </div>
                              <ChevronDown className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreditPurchaseModal;
